/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect } from 'react';

import type { CareerRole_PathwayLevel as ProficiencyLevel } from '__generated__/graphql-types';
import { CareerRole_MerchandisingJobCategory as CareerRoleCategory } from '__generated__/graphql-types';

import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';

import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import useCareerRoleDataCollection from 'bundles/unified-career-academy/hooks/useCareerRoleDataCollection';
import useCareerRolesDataLazy from 'bundles/unified-career-academy/hooks/useCareerRolesData';
import type { CareerRole } from 'bundles/unified-career-academy/hooks/useCareerRolesData';
import getQueryString from 'bundles/unified-career-academy/utils/getQueryString';
import CareerRoleCard from 'bundles/unified-common/components/CareerRoleCard';
import CourseraPlusUpsellCard from 'bundles/unified-common/components/coursera-plus-upsells/CourseraPlusUpsellCard';
import CareerAcademyModule from 'bundles/unified-common/modules/CareerAcademyModule';
import { useCourseraPlusUpsellPlacementData } from 'bundles/unified-common/providers/CourseraPlusUpsellPlacementDataProvider';
import type { CareerRolesCollectionType } from 'bundles/unified-common/types/careerRoles';
import { generateCacheKey, getDropdownMenuProps } from 'bundles/unified-common/utils/pathwaysFilters';

// eslint-disable-next-line @coursera-web/custom-rules/i18n-import-rule
import _t from 'i18n!nls/career-academy';

const DEFAULT_ROWS_TO_SHOW = 3;
const DEFAULT_ROWS_TO_SHOW_MOBILE = 12;
const INITIAL_ROLES_TO_FETCH = 12;
const TOTAL_ROLES_TO_FETCH = 60;

export const getChipFilters = () => [
  {
    id: 'all',
    filterName: _t('All'),
  },
  {
    id: CareerRoleCategory.SoftwareAndIt,
    filterName: _t('Software Engineering & IT'),
  },
  {
    id: CareerRoleCategory.Business,
    filterName: _t('Business'),
  },
  {
    id: CareerRoleCategory.SalesAndMarketing,
    filterName: _t('Sales & Marketing'),
  },
  {
    id: CareerRoleCategory.DataAndAnalytics,
    filterName: _t('Data Science & Analytics'),
  },
  {
    id: CareerRoleCategory.Healthcare,
    filterName: _t('Healthcare'),
  },
];

const styles = {
  container: css`
    margin-top: calc(var(--cds-spacing-600) * -1.5);
    padding: var(--cds-spacing-300) 0 !important;
    overflow: hidden;

    .cds-button-secondary {
      margin-top: var(--cds-spacing-150);
      margin-bottom: var(--cds-spacing-300);
    }

    ${breakpoints.down('md')} {
      margin-top: calc(var(--cds-spacing-800) * -1);
    }

    ${breakpoints.down('sm')} {
      margin-top: calc(var(--cds-spacing-400) * -1);
      padding: var(--cds-spacing-150) 0 !important;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-50) 0 !important;
    }
  `,
};

export const getChipFilterIndex = (
  chipFilters: { id: string; filterName: string }[],
  jobCategory: string | undefined
) => {
  const filterIndex = chipFilters.findIndex((chip) => chip.id.toLowerCase() === jobCategory?.toLowerCase());
  return filterIndex === -1 ? 0 : filterIndex;
};

export const getInitialDropdownValue = ({
  dropdownMenuOptions,
  proficiencyLevel,
  defaultValue,
}: {
  dropdownMenuOptions: { value: ProficiencyLevel; label: string }[];
  proficiencyLevel: string | undefined;
  defaultValue: ProficiencyLevel;
}): ProficiencyLevel => {
  const dropdownMenuOption = dropdownMenuOptions.find(
    (option) => option.value.toLowerCase() === proficiencyLevel?.toLowerCase()
  );
  return dropdownMenuOption?.value || defaultValue;
};

/**
 * CareerAcademyModuleRenderer makes sequential calls to fetch data for rendering role cards,
 * to avoid requiring Recs to rank pathways for roles that are behind the 'Show x more' button, thereby reducing query latency
 * The first call fetches the career roles collection without the associated pathways for each role.
 * The second call fetches the pathways for the first 12 roles returned from the first request.
 * Clicking on the 'Show x more' button will initiate requests to fetch pathways for the newly revealed roles.
 */
const CareerAcademyModuleRenderer = () => {
  const { query, pathname } = useLocation();
  const navigate = useNavigate();

  const chipFilters = getChipFilters();
  const dropdownMenuProps = getDropdownMenuProps();
  const [currentSelectedDropdownValue, setSelectedDropdownValue] = React.useState<ProficiencyLevel>(() =>
    getInitialDropdownValue({
      dropdownMenuOptions: dropdownMenuProps.options,
      proficiencyLevel: query?.level,
      defaultValue: dropdownMenuProps.defaultValue,
    })
  );
  const [currentChipFilterIndex, setCurrentChipFilterIndex] = React.useState(() =>
    getChipFilterIndex(chipFilters, query?.category)
  );

  const combinationKey = generateCacheKey(currentSelectedDropdownValue, currentChipFilterIndex);
  const defaultRowsRef = React.useRef<number>(DEFAULT_ROWS_TO_SHOW);
  const defaultMobileRowsRef = React.useRef<number>(DEFAULT_ROWS_TO_SHOW_MOBILE);

  const viewedCombinations = React.useRef<Map<string, CareerRolesCollectionType | undefined | null>>(new Map());

  const skipQuery = viewedCombinations.current.has(combinationKey);
  const [currentCollection, setCurrentCollection] = React.useState<CareerRolesCollectionType | undefined | null>();

  // Add pathways to their career roles in the local cache and update the currentCollection state to trigger a re-render to display the pathways
  const addRolesToLocalCache = useCallback<(data: CareerRole[] | undefined) => void>(
    (data: CareerRole[] | undefined) => {
      const selectedColleticon = viewedCombinations.current.get(combinationKey);

      if (data?.length && selectedColleticon) {
        const updatedCollection = {
          ...selectedColleticon,
          entities: selectedColleticon?.entities?.map((entity) => {
            const role = data?.find((item) => item?.id === entity?.id);
            if (role) {
              return { ...entity, pathways: role.pathways };
            }
            return entity;
          }),
        };

        viewedCombinations.current.set(combinationKey, updatedCollection);
        setCurrentCollection(updatedCollection);
      }
    },
    [viewedCombinations, combinationKey]
  );

  // Fetch pathways data for career roles
  const { fetchMoreCareerRolesData, loading: isLoadingMoreCareerRolesData } = useCareerRolesDataLazy({
    onCompleted: (data) => {
      addRolesToLocalCache(data);
    },
  });

  // Add the career roles collection to the local cache and fetch the pathways data for the first 12 roles.
  const addToLocalCache = (data: CareerRolesCollectionType | null) => {
    if (!viewedCombinations.current.has(combinationKey)) {
      viewedCombinations.current.set(combinationKey, data);

      // Reset the default rows when a new collection is fetched.
      defaultRowsRef.current = DEFAULT_ROWS_TO_SHOW;
      defaultMobileRowsRef.current = DEFAULT_ROWS_TO_SHOW_MOBILE;

      // If no collection data is returned, set currentCollection to null to display the "no results" message.
      if (!data) {
        setCurrentCollection(null);
        return;
      }

      const roleIds = data.entities?.slice(0, INITIAL_ROLES_TO_FETCH).map((role) => role?.id);
      if (roleIds?.length) {
        fetchMoreCareerRolesData({ variables: { ids: roleIds } });
      }
    }
  };

  // Fetch career roles collection data without associated pathways
  useCareerRoleDataCollection({
    ssr: false,
    skip: skipQuery,
    level: currentSelectedDropdownValue,
    limit: TOTAL_ROLES_TO_FETCH,
    jobCategory:
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      currentChipFilterIndex === 0 ? undefined : (chipFilters[currentChipFilterIndex].id as CareerRoleCategory),
    onCompleted: (data) => {
      addToLocalCache(data);
    },
    onError: () => {
      addToLocalCache(null);
    },
  });

  const handleExpandButtonClick = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    ({ numberOfItemsExpanded, cardsPerRow, numberOfItemsDisplayed }) => {
      const roleIds = currentCollection?.entities
        ?.slice(numberOfItemsDisplayed, numberOfItemsExpanded + numberOfItemsDisplayed)
        .map((role) => role?.id);

      // fetch pathways for the newly revealed roles
      if (roleIds?.length) {
        fetchMoreCareerRolesData({ variables: { ids: roleIds } });

        // Update the default rows to include the newly revealed roles;
        // otherwise, after React re-renders the ShowMoreGridSection, it will only render the original default rows.
        defaultRowsRef.current = Math.ceil((numberOfItemsExpanded + numberOfItemsDisplayed) / cardsPerRow);
        defaultMobileRowsRef.current = numberOfItemsExpanded + numberOfItemsDisplayed;
      }
    },
    [currentCollection, fetchMoreCareerRolesData]
  );

  const handleFilterChange = (index: number) => {
    navigate(
      {
        pathname,
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        search: getQueryString(query, 'category', chipFilters[index]?.id),
      },
      { replace: true }
    );

    setCurrentChipFilterIndex(index);
  };

  const handleDropdownChange = (value: ProficiencyLevel) => {
    navigate(
      {
        pathname,
        search: getQueryString(query, 'level', value),
      },
      { replace: true }
    );

    setSelectedDropdownValue(value);
  };

  // Update currentCollection when filters are changed
  useEffect(() => {
    setCurrentCollection(viewedCombinations.current.get(combinationKey));
    defaultRowsRef.current = DEFAULT_ROWS_TO_SHOW;
    defaultMobileRowsRef.current = DEFAULT_ROWS_TO_SHOW_MOBILE;
  }, [combinationKey]);

  const { ownsCourseraLite, ownsCourseraPlus } = useCourseraPlusProductOwnerships();
  const userHasSubscription = ownsCourseraLite || ownsCourseraPlus;

  let displayExperimentChanges = false;
  if (!userHasSubscription) {
    displayExperimentChanges = getShouldShowCourseraPlusMerchandisingPlacements();
  }

  const { upsellPlacement } = useCourseraPlusUpsellPlacementData();

  const upsellCard = {
    id: '1',
    renderComponent: (props: { index: number }) => (
      <CourseraPlusUpsellCard
        title={_t('Find your dream career by exploring them all.')}
        mobileBreakpoint="xs"
        eventingData={{ index: props.index, modulePlacement: upsellPlacement }}
      />
    ),
    position: 7,
  };

  let upsellCards: React.ComponentProps<typeof CareerAcademyModule>['upsellCards'] | undefined;

  if (displayExperimentChanges) {
    if (upsellPlacement === 'HIGH') {
      upsellCard.position = 7;
      upsellCards = [upsellCard];
    } else if (upsellPlacement === 'LOW') {
      upsellCard.position = 11;
      upsellCards = [upsellCard];
    } else if (!isAuthenticatedUser()) {
      // if user is in the experiment but not logged in, then we just always show a card in the 8th slot
      upsellCard.position = 7;
      upsellCards = [upsellCard];
    }
  }

  return (
    <PageGridContainer component="section" css={styles.container}>
      <Grid item xs={12}>
        <CareerAcademyModule
          filters={chipFilters}
          collection={currentCollection}
          dropdownMenu={dropdownMenuProps}
          ProductCardComponent={CareerRoleCard}
          currentFilterIndex={currentChipFilterIndex}
          onFilterChange={handleFilterChange}
          currentDropdownValue={currentSelectedDropdownValue}
          onDropdownMenuChange={handleDropdownChange}
          layout="text-top/image-bottom"
          backgroundColor="white"
          numOfEntriesPerCollection={TOTAL_ROLES_TO_FETCH}
          defaultRows={defaultRowsRef.current}
          defaultMobileRows={defaultMobileRowsRef.current}
          isCollectionLoading={currentCollection === undefined}
          onExpandButtonClick={handleExpandButtonClick}
          showPathwaysLoadingState={isLoadingMoreCareerRolesData}
          upsellCards={upsellCards}
        />
      </Grid>
    </PageGridContainer>
  );
};

export default CareerAcademyModuleRenderer;
